import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import firebase from "../config";

import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";

import { database } from "../config";
import CustomImage from "../reusable-components/Image";
import Layout from "../reusable-components/Layout";
import PaymentCard from "../reusable-components/payment/PaymentCard";
import ResumeForm from "../reusable-components/ResumeForm";
import TokenTierCard from "../reusable-components/TokenTierCard";
const promise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function WorkFlow() {
  const auth = getAuth();
  const [user, setUser] = useState();
  const [userTokenData, setUserTokenData] = useState();
  const provider = new GoogleAuthProvider();
  const {
    REACT_APP_RESUME_TOKEN_LIMIT,
    REACT_APP_COVERLETTER_TOKEN_LIMIT,
    REACT_APP_STRIPE_PERCENTAGE,
  } = process.env;

  const fileInputRef = useRef(null);
  const [resume, setResume] = useState(null);
  const [tierData, setTierData] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [showTiers, setShowTiers] = useState(false);
  const [isSuccessful, setSuccessful] = useState(false);
  const [coverLetter, setCoverLetter] = useState(false);
  const [showPayCard, setShowPayCard] = useState("");

  const [otherError, setOtherError] = useState({
    resume: false,
    selectedOption: false,
  });
  // const api_url = "http://localhost:4000";
  const api_url = process.env.REACT_APP_API_URL;

  async function googleSignInPopup() {
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error(error.code, error.message);
    }
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const allowedExtensions = [".pdf", ".doc", ".docx", ".txt"];
    const allowedMimes = [
      "application/pdf",
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "text/plain",
    ];

    const fileName = selectedFile.name.toLowerCase();
    const isValid =
      allowedExtensions.some((ext) => fileName.endsWith(ext)) &&
      allowedMimes.includes(selectedFile.type);

    if (isValid) {
      setResume(selectedFile);
    } else {
      alert("Please upload a PDF, DOC, DOCX, or TXT file.");
      e.target.value = null;
    }
  };

  function validateInput(values) {
    const valid =
      !!resume &&
      !!values.fullname &&
      !!values.email &&
      !!values.jobtitle &&
      (!!values.jobpostlink || !!values.jobdescription) &&
      !!selectedOption;
    return valid;
  }

  async function handleRequest(values, resetForm) {
    setSuccessful(true);
    setOtherError((prev) => ({
      ...prev,
      resume: resume === null,
      selectedOption: !selectedOption,
    }));

    if (!validateInput(values)) return;

    let requiredTokens = 0;
    const resumeLimit = parseInt(REACT_APP_RESUME_TOKEN_LIMIT, 10);
    const coverLetterLimit = parseInt(REACT_APP_COVERLETTER_TOKEN_LIMIT, 10);

    if (Number.isNaN(resumeLimit) || Number.isNaN(coverLetterLimit)) {
      console.error("Invalid token limits", {
        resumeLimit,
        coverLetterLimit,
      });
      alert("Configuration error. Please try again later.");
      return;
    }

    if (selectedOption?.toLowerCase() === "rewrite resume completely") {
      // Resume rewrite (with optional cover letter)
      requiredTokens = resumeLimit + (coverLetter ? coverLetterLimit : 0);
    } else if (coverLetter) {
      // Only cover letter selected
      requiredTokens = coverLetterLimit;
    }

    if (Number.isNaN(requiredTokens)) {
      console.error("requiredTokens is NaN", {
        resumeLimit,
        coverLetterLimit,
        selectedOption,
        coverLetter,
      });
      return;
    }

    if (
      !userTokenData &&
      (selectedOption?.toLowerCase() === "rewrite resume completely" ||
        coverLetter)
    ) {
      alert("Action required: Purchase tokens to access premium services!");
      setSuccessful(false);
      return;
    }

    if (requiredTokens > 0 && userTokenData?.token < requiredTokens) {
      // Check if user has enough tokens
      alert(
        `Insufficient tokens.\nYou have: ${userTokenData?.token} left.\nTop up your tokens to keep going!`
      );
      return;
    }

    const formData = new FormData();
    formData.append("document", resume); // file
    formData.append("fullname", values.fullname);
    formData.append("email", values.email);
    formData.append("jobtitle", values.jobtitle);
    formData.append("jobpostlink", values.jobpostlink || "");
    formData.append("jobdescription", values.jobdescription || "");
    formData.append("responsType", selectedOption); // array
    formData.append("coverLetter", coverLetter); // array

    const res = await fetch(`${api_url}/jobs/resume`, {
      method: "POST",
      body: formData, // ✅ just FormData, no JSON headers
      redirect: "follow",
    });

    const data = await res.json();

    if (data?.success) {
      const increment = firebase.firestore.FieldValue.increment;
      database
        .collection("ai-token")
        .doc(user.uid)
        .update({
          token: increment(-Math.max(0, requiredTokens)),
          usage: increment(
            selectedOption?.toLowerCase() === "rewrite resume completely" ||
              coverLetter
              ? -1
              : 0
          ),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

      setSuccessful(false);
      fileInputRef.current.value = "";
      setSelectedOption("");
      setCoverLetter(false);
      resetForm();
      alert(
        `${data?.n8nResponse?.data?.message}:\n Your job is on the way. You’ll receive an email once it’s ready.`
      );
    }

    if (data?.error) {
      resetForm();
      setSuccessful(false);
      alert(`${data?.error}. Please verify data and try again later.`);
    }
  }

  async function logout() {
    try {
      await signOut(auth);
      window.location.href = "/ai-automation/resume-screener";
    } catch (error) {
      console.error("Sign out error:", error);
    }
  }

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);

        const unsubscribeU = database
          .collection("ai-token")
          .onSnapshot((snapshot) => {
            const data = snapshot.docs
              .map((doc) => {
                if (doc.data().uid === user?.uid) {
                  return doc.data();
                }

                return null;
              })
              .filter(Boolean);

            setUserTokenData(data[0]);
          });

        return () => unsubscribeU();
      } else {
        console.log("User is signed out");
      }
    });
  }, [user, auth]);

  useEffect(() => {
    const unsubscribe = database
      .collection("ai-token-dict")
      .onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => doc.data());

        setTierData(data);
      });
    return () => unsubscribe();
  }, []);

  function updateUserToken(tier) {
    const increment = firebase.firestore.FieldValue.increment;

    database
      .collection("ai-token")
      .doc(user.uid)
      .update({
        token: increment(tier?.token),
        usage: increment(tier?.usage),
        subscription: tier?.name,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      })
      .catch(async (err) => {
        // 🔁 If document doesn't exist, create it
        if (err.code === "not-found") {
          await database
            .collection("ai-token")
            .doc(user.uid)
            .set({
              uid: user?.uid,
              displayName: user?.displayName,
              email: user?.email,
              token: tier?.token,
              usage: tier?.usage,
              subscription: tier?.name,
              paid: `$${(
                tier?.price * (1 + Number(REACT_APP_STRIPE_PERCENTAGE)) +
                0.3
              ).toFixed(2)} CAD`,
              createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            });
        }
      });

    setShowPayCard("");
  }

  return (
    <>
      <Helmet>
        <title>Wiingr | Resume Generation & Enhancer</title>
      </Helmet>
      <Layout isRag={true}>
        <div className="relative bg-[#181818] flex flex-col items-center justify-center h-[100vh] font-saira">
          <div className="w-[90%] sm:w-[70%] lg:w-1/2 2xl:w-1/3 overflow-hidden text-white h-[100%]">
            <div className="z-10 flex flex-row items-center gap-1 top-5 right-5 text-sm  text-white/30 cursor-pointer absolute">
              {user?.photoURL && (
                <CustomImage
                  width={30}
                  height={30}
                  alt={user?.name}
                  src={user?.photoURL}
                  className="rounded-full"
                />
              )}
              <div className="bg-[#181818] border-[0.5px] border-white/10 rounded-md hover:border-white/5 transition-all duration-500 hover:text-white/20">
                {user ? (
                  <button onClick={logout} className="px-2 py-1">
                    Sign Out
                  </button>
                ) : (
                  <button onClick={googleSignInPopup} className="px-2 py-1">
                    Sign In
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 mt-5 mb-3 w-full sm:w-[80%] text-center mx-auto">
              <p
                className="text-sm sm:text-lg tracking-wider justify-center bg-clip-text text-transparent 
  bg-gradient-to-r from-white/40 via-white/10 to-white/40
  bg-[length:200%_200%] animate-gradient-move uppercase"
              >
                AI Resume Generator & Enhancer
              </p>
              <p className="text-sm font-thin tracking-wider">
                An AI-powered automation designed with ATS compatibility in
                mind, focusing on human resources best practices and
                hiring-manager-proven resume structures to improve clarity,
                relevance, and interview readiness.
              </p>
            </div>
            <div className="relative w-full font-thin overflow-scroll no-scrollbar h-[65%] sm:h-[78%] mt-3 mb-16 flex flex-col">
              <ResumeForm
                user={user}
                otherError={otherError}
                coverLetter={coverLetter}
                selectedOption={selectedOption}
                fileInputRef={fileInputRef}
                handleRequest={handleRequest}
                handleFileChange={handleFileChange}
                setSelectedOption={setSelectedOption}
                setCoverLetter={setCoverLetter}
                setShowTiers={setShowTiers}
                userTokenData={userTokenData}
                isSuccessful={isSuccessful}
              />
            </div>
          </div>
          {showTiers && (
            <div className="h-full w-full bg-black absolute z-20 flex flex-col items-center justify-center overflow-hidden">
              <div className="flex justify-end w-full px-3 pt-3 pb-4">
                <svg
                  onClick={() => {
                    setShowPayCard("");
                    setShowTiers(false);
                  }}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-5 border-[0.5px] border-white/10 text-white rounded-full p-[5px] cursor-pointer"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <div className="flex flex-col gap-5 w-full px-4 overflow-auto h-full pb-6">
                {tierData?.length &&
                  tierData?.map((tier, idx) => (
                    <div className="w-full sm:w-[70%] lg:w-[50%] xl:w-[30%] 2xl:w-[25%] mx-auto">
                      <TokenTierCard
                        key={idx}
                        data={tier}
                        tier={tier?.name}
                        isSelected={showPayCard === idx}
                        onclick={() => {
                          setShowPayCard(idx);
                        }}
                      />
                      {showPayCard === idx && (
                        <div className="mt-2">
                          <Wrapper>
                            <Elements stripe={promise}>
                              <PaymentCard
                                isDay={false}
                                setIsOpen={() => {}}
                                due={tier?.price}
                                isResumePayment={true}
                                extraFunction={() => updateUserToken(tier)}
                              />
                            </Elements>
                          </Wrapper>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </Layout>
    </>
  );
}

const Wrapper = styled.section`
  form {
    width: 100%;
    align-self: center;
    box-shadow: 0px 0px 0px 0.5px rgba(50, 50, 93, 0.1),
      0px 2px 5px 0px rgba(50, 50, 93, 0.1),
      0px 1px 1.5px 0px rgba(0, 0, 0, 0.07);
    border-radius: 7px;
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 30px;
  }

  input {
    border-radius: 6px;
    margin-bottom: 6px;
    padding: 12px;
    border: 1px solid rgba(50, 50, 93, 0.1);
    max-height: 44px;
    font-size: 16px;
    width: 100%;
    background: white;
    box-sizing: border-box;
  }

  .result-message {
    line-height: 22px;
    font-size: 16px;
  }

  .result-message a {
    color: rgb(89, 111, 214);
    font-weight: 600;
    text-decoration: none;
  }

  .hidden {
    display: none;
  }

  #card-error {
    color: rgb(105, 115, 134);
    font-size: 16px;
    line-height: 20px;
    margin-top: 12px;
    text-align: center;
  }

  #card-element {
    max-height: 44px;
    width: 100%;
    box-sizing: border-box;
  }

  #payment-request-button {
    margin-bottom: 32px;
  }

  button:hover {
    filter: contrast(115%);
  }

  button:disabled {
    opacity: 0.5;
    cursor: default;
  }

  /* spinner/processing state, errors */
  .spinner,
  .spinner:before,
  .spinner:after {
    border-radius: 50%;
  }

  .spinner {
    color: #fff;
    font-size: 22px;
    text-indent: -99999px;
    margin: 0px auto;
    position: relative;
    width: 20px;
    height: 20px;
    box-shadow: inset 0 0 0 2px;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
  }

  .spinner:before,
  .spinner:after {
    position: absolute;
    content: "";
  }

  .spinner:before {
    width: 10.4px;
    height: 20.4px;
    background: undefined;
    border-radius: 20.4px 0 0 20.4px;
    top: -0.2px;
    left: -0.2px;
    -webkit-transform-origin: 10.4px 10.2px;
    transform-origin: 10.4px 10.2px;
    -webkit-animation: loading 2s infinite ease 1.5s;
    animation: loading 2s infinite ease 1.5s;
  }

  .spinner:after {
    width: 10.4px;
    height: 10.2px;
    background: undefined;
    border-radius: 0 10.2px 10.2px 0;
    top: -0.1px;
    left: 10.2px;
    -webkit-transform-origin: 0px 10.2px;
    transform-origin: 0px 10.2px;
    -webkit-animation: loading 2s infinite ease;
    animation: loading 2s infinite ease;
  }

  @keyframes loading {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
`;

export default WorkFlow;
